import { c as _c } from "react/compiler-runtime";
import { useAppContext } from '@/contexts/AppContext/AppContext';
import type { ContentType } from '@/interfaces';
import { BrowserHttpClient } from '@/services/Http/BrowserHttpClient';
import { useEffect, useRef } from 'react';
const DWELL_THRESHOLD_MS = 30_000;
const TICK_MS = 1_000;
const httpClient = new BrowserHttpClient();
interface IProgress {
  progressPercent: number | null;
  progressDetail: string;
}
export const trackView = (contentId: string, contentType: ContentType, getProgress: (() => IProgress) | null, keepalive = false) => {
  const {
    progressPercent,
    progressDetail
  } = getProgress ? getProgress() : {
    progressPercent: null,
    progressDetail: ''
  };
  if (getProgress && progressPercent === null) {
    return; // Don't track if progressPercent is null when getProgress is provided, as it indicates that the content is not yet viewable.
  }
  httpClient.post({
    path: '/users/content_view',
    body: {
      contentId,
      contentType,
      progressPercent,
      progressDetail
    },
    keepalive
  });
};
export const useUserContentViewTracker = (contentId, contentType, isActive) => {
  const $ = _c(5);
  const isMounted = useRef(false);
  let t0;
  let t1;
  if ($[0] !== contentId || $[1] !== contentType || $[2] !== isActive) {
    t0 = () => {
      if (!isMounted.current) {
        isMounted.current = true;
        return;
      }
      if (!isActive) {
        return;
      }
      trackView(contentId, contentType, null);
    };
    t1 = [contentId, contentType, isActive];
    $[0] = contentId;
    $[1] = contentType;
    $[2] = isActive;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
};
export const useUserContentProgressTracker = (contentId: string | null, contentType: ContentType | null, isActive: boolean, getProgress: () => {
  progressPercent: number | null;
  progressDetail: string;
}, dwellTime: number = DWELL_THRESHOLD_MS) => {
  const {
    isOfflineSession
  } = useAppContext();
  const getProgressRef = useRef(getProgress);
  getProgressRef.current = getProgress;
  useEffect(() => {
    if (isOfflineSession) return;

    // Skip if not active or if contentId/contentType is not provided
    if (!isActive || !contentId || !contentType) return;
    if (dwellTime === 0) {
      trackView(contentId, contentType, getProgressRef.current);
      return;
    }
    let elapsed = 0;
    const tick = () => {
      if (document.hidden) {
        // Track last progress before user left the page, then resets elapsed time to avoid double counting
        if (elapsed > 0) {
          trackView(contentId, contentType, getProgressRef.current);
          elapsed = 0;
        }
        // Skip tracking when the document is hidden
        return;
      }
      elapsed += TICK_MS;
      if (elapsed < dwellTime) return;
      elapsed = 0;
      trackView(contentId, contentType, getProgressRef.current);
    };
    const interval = setInterval(tick, TICK_MS);
    const handlePageHide = () => {
      if (elapsed > 0) {
        trackView(contentId, contentType, getProgressRef.current, true);
        elapsed = 0;
      }
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      clearInterval(interval);
      window.removeEventListener('pagehide', handlePageHide);
      if (elapsed > 0) {
        trackView(contentId, contentType, getProgressRef.current);
      }
    };
  }, [contentId, contentType, isActive, dwellTime, isOfflineSession]);
};